import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, Globe, Webhook, Copy, Check, ExternalLink, 
  RefreshCw, Shield, Zap, Building2, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function PlatformSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Form state
  const [publicUrl, setPublicUrl] = useState('');
  const [zapierEnabled, setZapierEnabled] = useState(true);
  const [bpSyncEnabled, setBpSyncEnabled] = useState(true);
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setPublicUrl(response.data.public_url || '');
      setZapierEnabled(response.data.zapier_enabled);
      setBpSyncEnabled(response.data.builderprime_sync_enabled);
      setCompanyName(response.data.company_name || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.put(`${API}/platform-settings`, {
        public_url: publicUrl,
        zapier_enabled: zapierEnabled,
        builderprime_sync_enabled: bpSyncEnabled,
        company_name: companyName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'key') {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } else {
      setCopiedUrl(type);
      setTimeout(() => setCopiedUrl(null), 2000);
    }
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Platform Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure integrations, webhooks, and system settings
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Changes
        </Button>
      </div>

      {/* Public URL Setting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Public URL
          </CardTitle>
          <CardDescription>
            The public URL where Rhino-1 is accessible. Used for webhooks and external integrations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="publicUrl">Production URL</Label>
            <div className="flex gap-2">
              <Input
                id="publicUrl"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                placeholder="https://your-domain.emergent.host"
                className="flex-1"
                data-testid="public-url-input"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(publicUrl, 'publicUrl')}
                disabled={!publicUrl}
              >
                {copiedUrl === 'publicUrl' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Example: https://call-assistant-30.emergent.host
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Zapier Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" />
            Zapier Integration
            {settings?.zapier_webhook_key_configured ? (
              <Badge className="bg-green-500">Configured</Badge>
            ) : (
              <Badge variant="destructive">Not Configured</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Connect Builder Prime to Rhino-1 via Zapier for real-time data sync
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Zapier Webhooks</Label>
              <p className="text-xs text-muted-foreground">Accept incoming webhooks from Zapier</p>
            </div>
            <Switch
              checked={zapierEnabled}
              onCheckedChange={setZapierEnabled}
              data-testid="zapier-toggle"
            />
          </div>

          {/* API Key */}
          {settings?.zapier_webhook_key && (
            <div className="space-y-2">
              <Label>API Key for Zapier</Label>
              <div className="flex gap-2">
                <code className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg font-mono text-sm break-all">
                  {settings.zapier_webhook_key}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(settings.zapier_webhook_key, 'key')}
                >
                  {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Add this as header <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">X-Zapier-Key</code> in your Zaps
              </p>
            </div>
          )}

          {/* Webhook Endpoints */}
          {settings?.webhook_endpoints && publicUrl && (
            <div className="space-y-3">
              <Label>Webhook Endpoints</Label>
              <div className="space-y-2">
                {Object.entries(settings.webhook_endpoints).map(([name, url]) => (
                  <div key={name} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <Badge variant="outline" className="min-w-[120px] justify-center">
                      {name.replace(/_/g, ' ')}
                    </Badge>
                    <code className="flex-1 text-xs font-mono truncate">{url}</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => copyToClipboard(url, name)}
                    >
                      {copiedUrl === name ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!publicUrl && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                ⚠️ Set your Public URL above to see the complete webhook endpoints
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Builder Prime Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-500" />
            Builder Prime Sync
          </CardTitle>
          <CardDescription>
            Automatic background sync with Builder Prime
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Auto-Sync</Label>
              <p className="text-xs text-muted-foreground">
                Sync leads and projects every {settings?.builderprime_sync_interval_minutes || 15} minutes
              </p>
            </div>
            <Switch
              checked={bpSyncEnabled}
              onCheckedChange={setBpSyncEnabled}
              data-testid="bp-sync-toggle"
            />
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Last sync: {settings?.updated_at ? new Date(settings.updated_at).toLocaleString() : 'Never'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Zapier Setup Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-purple-500" />
            Zapier Setup Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h4 className="font-semibold mb-2">🔥 Most Important Zap: Project Updated</h4>
              <p className="text-sm text-muted-foreground mb-3">
                This syncs contract amounts in real-time for accurate revenue tracking
              </p>
              <ol className="text-sm space-y-2 list-decimal list-inside">
                <li>In Zapier, create new Zap</li>
                <li>Trigger: <strong>Builder Prime → Project Updated</strong></li>
                <li>Action: <strong>Webhooks by Zapier → POST</strong></li>
                <li>URL: <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded text-xs">{publicUrl || '<your-url>'}/api/webhooks/zapier/project/updated</code></li>
                <li>Add header: <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded text-xs">X-Zapier-Key: {settings?.zapier_webhook_key?.slice(0, 10)}...</code></li>
                <li>Map fields: builderprime_id, customer_name, status, total_contracted_amount</li>
              </ol>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h5 className="font-medium text-sm mb-1">New Lead → Rhino-1</h5>
                <p className="text-xs text-muted-foreground">Sync new leads in real-time</p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h5 className="font-medium text-sm mb-1">Lead Updated → Rhino-1</h5>
                <p className="text-xs text-muted-foreground">Track lead status changes</p>
              </div>
            </div>
          </div>

          <Button variant="outline" className="w-full" asChild>
            <a href="https://zapier.com/apps/builderprime/integrations" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Builder Prime on Zapier
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default PlatformSettings;
